import _, { sample, times } from 'lodash';
import moment from 'moment';
import { DateObject } from 'react-multi-date-picker';
import Cookies from 'universal-cookie';
import { create } from 'zustand';
import authAxios from '@services/authAxios';
import { useDownloadStore } from '@hooks/useDownloadStore';

export const fetchGlobalStyles = (environment) => {
    environment === 'development' ? require('../@styles/development-styles') : require('../@styles/production-styles');

    return environment;
};

export const debounce = (func, timeout = 300) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
};

export const getToken = () => {
    const cookies = new Cookies();

    const tokenCookies = cookies.get('userToken');

    return tokenCookies;
};

export const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
    }).format(value);
};

export const parseCurrency = (value) => {
    if (value === undefined || value === null) return '';
    const digits = String(value).replace(/[^0-9]/g, '');
    return digits === '' ? '' : Number(digits);
};

export const createStore = (initialState) => {
    return create((set) => ({
        ...initialState,
        set: (newState) =>
            set((state) => ({
                ...state,
                ...newState,
            })),
        reset: () => set(initialState),
    }));
};

export const checkFilterActive = (filters, initialState) => {
    return Object.keys(initialState).some((key) => {
        return !_.isEmpty(filters[key]);
    });
};

export const formatDate = (date, format = 'DD MMM YYYY') => (date ? moment(date).format(format) : '-');

export const excludeKeys = (params, keys) => {
    return Object.fromEntries(
        Object.entries(params).filter(([k, v]) => {
            if (v === '') return false;
            if (keys.includes(k)) return false;
            return true;
        }),
    );
};

export const dateFormatter = (date, format = 'DD MMM YYYY') => (date ? new DateObject(date).format(format) : null);

export const validateObjectFields = (values, excludeKeys = []) => {
    const filtered = _.omit(values, excludeKeys);
    const invalids = {};

    Object.entries(filtered).forEach(([key, val]) => {
        let normalized = val;
        if (val && typeof val === 'object') {
            normalized = val.format?.('YYYY-MM-DD') || val;
        } else if (_.isString(val)) {
            normalized = _.trim(val);
        }

        if (_.isEmpty(normalized)) {
            invalids[key] = true;
        }
    });

    return {
        invalids,
        hasInvalid: Object.keys(invalids).length > 0,
    };
};

export const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' Kb';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' Mb';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' Gb';
};

export const fileDownloader = async ({ url, fileName, method = 'GET', body = null, signal = null, params = null }) => {
    const { setProgress, finishDownload } = useDownloadStore.getState();

    try {
        const response = await authAxios({
            url,
            method,
            data: body,
            signal,
            responseType: 'blob',
            params: params,
            onDownloadProgress: (progressEvent) => {
                if (progressEvent.lengthComputable) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProgress(percent);
                }
            },
        });

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        if (authAxios.isCancel?.(error) || error.name === 'CanceledError') {
            console.warn('Download aborted.');
            return;
        }
        console.error('File download failed:', error);
        throw error;
    } finally {
        if (finishDownload) finishDownload();
    }
};

const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
export const randomStringGenerator = (length = 32) => times(length, () => sample(chars)).join('');

export const roleCheck = () => {
    const cookies = new Cookies();
    const role = cookies.get('role');
    return role;
};
